import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";

export interface Customer {
  id: number;
  name: string;
  mobileNumber: string | null;
  email: string | null;
  instagramHandle: string | null;
  address: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    orders: number;
  };
}

export interface CustomerWithOrders extends Customer {
  orders: Array<{
    id: number;
    orderNumber: string;
    orderDate: string;
    status: string;
    total: number;
    paymentStatus: string;
  }>;
}

export interface CreateCustomerDTO {
  name: string;
  mobileNumber?: string;
  email?: string;
  instagramHandle?: string;
  address?: string;
  notes?: string;
}

export interface UpdateCustomerDTO {
  id: number;
  data: Partial<CreateCustomerDTO>;
}

export function useCustomers() {
  return useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      return apiClient.get<Customer[]>("/customers");
    },
  });
}

export function useCustomer(id: number) {
  return useQuery({
    queryKey: ["customers", id],
    queryFn: async () => {
      return apiClient.get<CustomerWithOrders>(`/customers/${id}`);
    },
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCustomerDTO) => {
      return apiClient.post<Customer>("/customers", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateCustomerDTO) => {
      return apiClient.put<Customer>(`/customers/${id}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customers", variables.id] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
